import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { 
  TrendingUp, 
  Calendar,
  ArrowRight,
  Target,
  BookOpen,
  Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Progress() {
  const { token } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [sessionsRes, scenariosRes] = await Promise.all([
        axios.get(`${API}/sessions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/scenarios`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSessions(sessionsRes.data.filter(s => s.overall_score));
      setScenarios(scenariosRes.data);
    } catch (error) {
      console.error('Failed to load progress data:', error);
    }
    setLoading(false);
  };

  // Prepare chart data
  const chartData = sessions.slice(-10).map((session, i) => ({
    session: i + 1,
    confidence: session.scores?.confidence || 0,
    clarity: session.scores?.clarity || 0,
    tone: session.scores?.tone || 0,
    effectiveness: session.scores?.effectiveness || 0,
  }));

  // Get recommendation based on lowest average category
  const getRecommendation = () => {
    if (sessions.length === 0) return null;
    
    const avgScores = {
      confidence: 0,
      clarity: 0,
      tone: 0,
      effectiveness: 0,
    };
    
    sessions.forEach(s => {
      if (s.scores) {
        avgScores.confidence += s.scores.confidence || 0;
        avgScores.clarity += s.scores.clarity || 0;
        avgScores.tone += s.scores.tone || 0;
        avgScores.effectiveness += s.scores.effectiveness || 0;
      }
    });
    
    Object.keys(avgScores).forEach(k => {
      avgScores[k] = avgScores[k] / sessions.length;
    });
    
    const lowest = Object.entries(avgScores).reduce((a, b) => a[1] < b[1] ? a : b);
    
    const recommendations = {
      confidence: { text: 'Practice speaking with more conviction', scenario: scenarios[0] },
      clarity: { text: 'Focus on stating your goals clearly upfront', scenario: scenarios[0] },
      tone: { text: 'Work on maintaining a calm, respectful tone', scenario: scenarios[1] },
      effectiveness: { text: 'Practice closing conversations with clear next steps', scenario: scenarios[1] },
    };
    
    return {
      category: lowest[0],
      score: Math.round(lowest[1]),
      ...recommendations[lowest[0]]
    };
  };

  const recommendation = getRecommendation();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00E085]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="progress-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-[#111827]">Your Progress</h1>
          <p className="text-gray-600 mt-1">Track your communication improvement over time</p>
        </div>

        {/* Stats Summary */}
        <div className="grid sm:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <BookOpen className="w-8 h-8 text-[#00E085] mb-4" />
            <p className="text-3xl font-bold text-[#111827]">{sessions.length}</p>
            <p className="text-gray-500">Total Sessions</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <Target className="w-8 h-8 text-[#6366F1] mb-4" />
            <p className="text-3xl font-bold text-[#111827]">
              {sessions.length > 0 
                ? Math.round(sessions.reduce((a, b) => a + (b.overall_score || 0), 0) / sessions.length)
                : 0
              }
            </p>
            <p className="text-gray-500">Average Score</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <TrendingUp className="w-8 h-8 text-[#F59E0B] mb-4" />
            <p className="text-3xl font-bold text-[#111827]">
              {sessions.length >= 2
                ? `${(sessions[sessions.length - 1]?.overall_score || 0) - (sessions[0]?.overall_score || 0) >= 0 ? '+' : ''}${(sessions[sessions.length - 1]?.overall_score || 0) - (sessions[0]?.overall_score || 0)}`
                : '—'
              }
            </p>
            <p className="text-gray-500">Score Change</p>
          </div>
        </div>

        {/* Category Progress Chart */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100" data-testid="category-chart">
          <h3 className="text-lg font-semibold text-[#111827] mb-6">Category Progress</h3>
          {chartData.length > 0 ? (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis 
                    dataKey="session" 
                    stroke="#9CA3AF" 
                    fontSize={12}
                    tickLine={false}
                  />
                  <YAxis 
                    stroke="#9CA3AF" 
                    fontSize={12}
                    tickLine={false}
                    domain={[0, 100]}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      borderRadius: '12px', 
                      border: 'none', 
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="confidence" stroke="#00E085" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="clarity" stroke="#6366F1" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="tone" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="effectiveness" stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-80 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Complete sessions to see your progress</p>
              </div>
            </div>
          )}
        </div>

        {/* Session Timeline */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100" data-testid="session-timeline">
          <h3 className="text-lg font-semibold text-[#111827] mb-6">Session History</h3>
          {sessions.length > 0 ? (
            <div className="space-y-4">
              {sessions.slice().reverse().slice(0, 10).map((session) => {
                const scenario = scenarios.find(s => s.id === session.scenario_id);
                return (
                  <Link
                    key={session.id}
                    to={`/feedback/${session.id}`}
                    className="flex items-center justify-between p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        (session.overall_score || 0) >= 80 ? 'bg-green-100 text-green-600' :
                        (session.overall_score || 0) >= 60 ? 'bg-yellow-100 text-yellow-600' :
                        'bg-red-100 text-red-600'
                      }`}>
                        <span className="font-bold">{session.overall_score || 0}</span>
                      </div>
                      <div>
                        <p className="font-medium text-[#111827]">{scenario?.title || 'Unknown Scenario'}</p>
                        <p className="text-sm text-gray-500">
                          {new Date(session.started_at).toLocaleDateString()} • {Math.floor((session.duration_seconds || 0) / 60)} min
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400" />
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No sessions yet</p>
            </div>
          )}
        </div>

        {/* Recommendation Card */}
        {recommendation && (
          <div className="bg-gradient-to-r from-[#00E085]/10 to-[#6366F1]/10 rounded-3xl p-8" data-testid="recommendation-card">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-6 h-6 text-[#00E085]" />
              <span className="font-semibold text-[#111827]">Recommended Next</span>
            </div>
            <p className="text-gray-700 mb-4">
              Your <strong>{recommendation.category}</strong> score averages {recommendation.score}. {recommendation.text}
            </p>
            {recommendation.scenario && (
              <Link to={`/scenario/${recommendation.scenario.id}`}>
                <Button className="bg-[#111827] text-white hover:bg-[#1F2937] rounded-full px-6">
                  Practice {recommendation.scenario.title}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            )}
          </div>
        )}

        {/* Educational Disclaimer */}
        <div className="rounded-2xl p-4 mt-8 text-center" style={{ background: '#FAF8F5', border: '1px solid rgba(0,0,0,0.06)' }} data-testid="progress-disclaimer">
          <p className="text-xs leading-relaxed" style={{ color: '#8B7355' }}>
            Scores and recommendations are for educational purposes only. They reflect communication skill practice and do not constitute financial, investment, or professional advice.
          </p>
        </div>
      </div>
    </Layout>
  );
}
